/**
 * English: Home dashboard for unified admin — orgs, shortcuts, status (not station-scoped).
 * Thai: หน้าหลัก admin รวม — องค์กร ทางลัด สถานะ (ไม่ผูกสถานีเดียว)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InboxIcon, ArrowRightIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useStations } from '../hooks/useStations';
import { useTaskRuns } from '../hooks/useTaskRuns';
import { useApiHealth } from '../hooks/useApiHealth';
import { useUsageHistory, DailyUsage } from '../hooks/useUsage';
import DashboardHero from '../components/dashboard/DashboardHero';
import DashboardStats, { summarizeRuns } from '../components/dashboard/DashboardStats';
import QuickActions from '../components/dashboard/QuickActions';
import RecentTasks from '../components/dashboard/RecentTasks';
import { Task } from '../types';
import PageShell from '../components/layout/PageShell';
import { isTaskRunVisibleInInbox } from '../utils/taskRunMode';
import { API_BASE, ENDPOINTS } from '../config/api';
import { parseJsonResponse } from '../utils/httpJson';
import { useAuth } from '../context/AuthContext';
import { getStoredLoginUsername } from '../utils/deviceCommandPassword';
import {
  type ServiceStatus,
  type SystemStatusResponse,
  summarizeServices,
  buildUsageBars,
} from './dashboardHelpers';

export type { ServiceStatus, SystemStatusResponse };
export { summarizeServices, buildUsageBars };

type EnabledOrg = { org_name: string; display_name: string };

function useDashboardSystemStatus() {
  const { token } = useAuth();
  const [services, setServices] = useState<Record<string, ServiceStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = API_BASE
      ? `${API_BASE}${ENDPOINTS.systemStatus}`
      : new URL(ENDPOINTS.systemStatus, window.location.origin).toString();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    (async () => {
      try {
        const res = await fetch(url, { headers });
        const data = await parseJsonResponse<SystemStatusResponse>(res, { url });
        if (cancelled) return;
        if (!res.ok) {
          setError(`HTTP ${res.status}`);
          setServices({});
        } else {
          setServices(data.services || {});
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'status failed');
          setServices({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { services, loading, error };
}

function useEnabledOrganizations() {
  const { token } = useAuth();
  const [orgs, setOrgs] = useState<EnabledOrg[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(ENDPOINTS.adminOrganizations, {
        credentials: 'include',
        headers,
      });
      const data = (await res.json().catch(() => ({}))) as { organizations?: EnabledOrg[] };
      if (res.ok) setOrgs(data.organizations || []);
      else setOrgs([]);
    } catch {
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { orgs, loading };
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { stations } = useStations();
  const { runs } = useTaskRuns(12);
  const { data: health, loading: healthLoading, error: healthError } = useApiHealth(false);
  const { services, loading: statusLoading, error: statusError } = useDashboardSystemStatus();
  const { data: usage, loading: usageLoading } = useUsageHistory(7);
  const { orgs: enabledOrgs, loading: orgsLoading } = useEnabledOrganizations();
  const username = getStoredLoginUsername() || '';

  const healthForUi = {
    loading: healthLoading,
    error: healthError,
    status: health?.status ? (health.status === 'ok' ? ('ok' as const) : ('degraded' as const)) : null,
    version: health?.version,
  };

  const { total: taskTotal, errorCount: taskErrorCount } = useMemo(() => summarizeRuns(runs), [runs]);

  const recentTasks: Task[] = useMemo(
    () =>
      runs
        .filter((r) => isTaskRunVisibleInInbox(r.mode))
        .map((r) => ({
          id: r.task_id,
          message: r.prompt || t('common.noPrompt'),
          station: r.station_id || '—',
          status: r.status,
          time: new Date(r.finished_at).toLocaleString(),
        })),
    [runs, t],
  );

  const statusSummary = useMemo(() => summarizeServices(services), [services]);
  const usageBars = useMemo(() => buildUsageBars((usage?.daily as DailyUsage[]) || []), [usage]);

  return (
    <PageShell wide>
      <div className="space-y-6">
        <DashboardHero
          username={username}
          enabledOrgCount={enabledOrgs.length}
          health={healthForUi}
        />

        <DashboardStats
          enabledOrgCount={enabledOrgs.length}
          stationCount={stations.length}
          taskTotal={taskTotal}
          taskErrorCount={taskErrorCount}
          health={healthForUi}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <QuickActions />

            <section className="overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
              <div className="flex items-center justify-between gap-2 border-b border-app-border/60 bg-app-muted/15 px-5 py-4 sm:px-6">
                <div>
                  <h2 className="text-base font-semibold text-ink">
                    {t('dashboard.enabledOrgsTitle', { defaultValue: 'องค์กรที่เปิดใช้' })}
                  </h2>
                  <p className="mt-0.5 text-sm text-app-text-dim">
                    {t('dashboard.enabledOrgsHint', {
                      defaultValue: 'ใช้แชทได้กับองค์กรที่ Limbic ตั้ง is_active=true',
                    })}
                  </p>
                </div>
                <Link
                  to="/organizations"
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm font-medium text-app-accent transition-colors hover:bg-app-muted/40"
                >
                  <BuildingOffice2Icon className="h-4 w-4" />
                  {t('dashboard.manageOrgs', { defaultValue: 'ดูองค์กร' })}
                </Link>
              </div>
              <div className="p-5 sm:p-6">
                {orgsLoading ? (
                  <p className="text-sm text-app-text-dim">{t('common.loading')}</p>
                ) : enabledOrgs.length === 0 ? (
                  <p className="text-sm text-app-text-sec">
                    {t('dashboard.enabledOrgsEmpty', {
                      defaultValue: 'ไม่พบองค์กรที่ active จาก Limbic',
                    })}{' '}
                    <Link to="/organizations" className="font-medium text-app-accent underline">
                      {t('sidebar.menu.organizations')}
                    </Link>
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {enabledOrgs.map((o) => (
                      <li key={o.org_name}>
                        <Link
                          to="/chat-test"
                          className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-bg/60 px-3 py-2 text-sm transition-colors hover:border-app-accent/40 hover:bg-app-accent/5"
                        >
                          <span className="font-mono font-medium text-app-text">{o.org_name}</span>
                          {o.display_name && o.display_name !== o.org_name && (
                            <span className="text-xs text-app-text-dim truncate max-w-[10rem]">
                              {o.display_name}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
              <div className="flex items-center justify-between gap-2 border-b border-app-border/60 bg-app-muted/15 px-5 py-4 sm:px-6">
                <div>
                  <h2 className="text-base font-semibold text-ink">{t('dashboard.recentTasks')}</h2>
                  <p className="mt-0.5 text-sm text-app-text-dim">
                    {t('dashboard.recentSectionHintHome', {
                      defaultValue: 'งานล่าสุดจากระบบ',
                    })}
                  </p>
                </div>
                <Link
                  to="/tasks"
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm font-medium text-app-accent transition-colors hover:bg-app-muted/40"
                >
                  <InboxIcon className="h-4 w-4" />
                  {t('dashboard.recentTasksLink')}
                </Link>
              </div>
              <div className="p-5 sm:p-6">
                <RecentTasks tasks={recentTasks} embedded />
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <section className="rounded-2xl border border-app-border/80 bg-app-surface p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-ink">
                  {t('dashboard.systemStatus', { defaultValue: 'สถานะระบบ' })}
                </h2>
                <Link to="/status" className="inline-flex items-center gap-1 text-xs font-medium text-app-accent hover:underline">
                  {t('common.viewAll', { defaultValue: 'ดูทั้งหมด' })}
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </Link>
              </div>

              {statusLoading ? (
                <p className="py-4 text-sm text-app-text-dim">{t('common.loading', { defaultValue: 'กำลังโหลด…' })}</p>
              ) : statusError ? (
                <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-600 dark:text-rose-400">
                  {t('dashboard.systemStatusError', { error: statusError, defaultValue: 'โหลดสถานะไม่สำเร็จ: {{error}}' })}
                </p>
              ) : statusSummary.total === 0 ? (
                <p className="py-4 text-sm text-app-text-dim">{t('dashboard.systemStatusEmpty', { defaultValue: 'ไม่มีข้อมูลบริการ' })}</p>
              ) : (
                <>
                  <p className="mb-3 text-xs text-app-text-dim">
                    {t('dashboard.systemStatusSummary', {
                      ok: statusSummary.okCount,
                      total: statusSummary.total,
                      defaultValue: 'ปกติ {{ok}} / {{total}} บริการ',
                    })}
                  </p>
                  <ul className="divide-y divide-app-border/50">
                    {statusSummary.rows.map((row) => (
                      <li key={row.name} className="flex items-center justify-between gap-3 py-2">
                        <span className="flex min-w-0 items-center gap-2">
                          <span
                            className={`inline-block h-2 w-2 shrink-0 rounded-full ${row.ok ? 'bg-emerald-500' : 'bg-rose-500'}`}
                          />
                          <span className="truncate text-sm text-app-text">{row.name}</span>
                        </span>
                        <span className="shrink-0 text-xs tabular-nums text-app-text-dim">{row.latency_ms}ms</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>

            <section className="rounded-2xl border border-app-border/80 bg-app-surface p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-ink">
                  {t('dashboard.usageSnapshot', { defaultValue: 'การใช้งาน LLM (7 วัน)' })}
                </h2>
                <Link to="/usage" className="inline-flex items-center gap-1 text-xs font-medium text-app-accent hover:underline">
                  {t('common.viewAll', { defaultValue: 'ดูทั้งหมด' })}
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </Link>
              </div>

              {usageLoading ? (
                <p className="py-4 text-sm text-app-text-dim">{t('common.loading', { defaultValue: 'กำลังโหลด…' })}</p>
              ) : !usage || usage.error ? (
                <p className="py-4 text-sm text-app-text-dim">{t('dashboard.usageEmpty', { defaultValue: 'ยังไม่มีข้อมูลการใช้งาน' })}</p>
              ) : (
                <>
                  <div className="flex h-16 items-end gap-1">
                    {usageBars.length > 0 ? (
                      usageBars.map((h, i) => (
                        <span
                          key={i}
                          className="flex-1 rounded-t bg-app-accent/30"
                          style={{ height: `${Math.max(4, h)}%` }}
                          title={`${usage.daily[i]?.date ?? ''}: ${usage.daily[i]?.tokens ?? 0} tokens`}
                        />
                      ))
                    ) : (
                      <span className="text-xs text-app-text-dim">{t('dashboard.usageEmpty', { defaultValue: 'ยังไม่มีข้อมูลการใช้งาน' })}</span>
                    )}
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-sm font-semibold tabular-nums text-ink">{usage.total_tokens.toLocaleString()}</p>
                      <p className="text-[10px] uppercase tracking-wide text-app-text-dim">{t('dashboard.usageTokens', { defaultValue: 'tokens' })}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold tabular-nums text-ink">{usage.total_calls.toLocaleString()}</p>
                      <p className="text-[10px] uppercase tracking-wide text-app-text-dim">{t('dashboard.usageCalls', { defaultValue: 'calls' })}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold tabular-nums text-ink">${usage.total_cost_usd.toFixed(2)}</p>
                      <p className="text-[10px] uppercase tracking-wide text-app-text-dim">{t('dashboard.usageCost', { defaultValue: 'cost' })}</p>
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
